import { createContext, useContext } from "react";
import { getValidToken } from "@utils";
import { FetchError } from "./fetchError";
import { API_URL } from "../environment";

interface IFetchContext {
  <T>(pathname: string, init?: RequestInit): Promise<T>;
}

const FetchContext = createContext<IFetchContext>(undefined as unknown as IFetchContext);

export function useFetch() {
  const context = useContext(FetchContext);

  if (!context) throw new Error("useFetch outside provider");

  return context;
}

// An error response is not guaranteed to carry a JSON body: API Gateway and
// CloudFront answer with plain text or HTML. Parsing it eagerly threw a
// SyntaxError that replaced the FetchError, hiding the status from callers.
async function parseErrorBody(response: Response): Promise<unknown> {
  const body = await response.text();

  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
}

interface FetchProviderProps {
  children: React.ReactNode;
}

export function FetchProvider({ children }: FetchProviderProps) {
  async function customFetch<T>(pathname: string, init?: RequestInit): Promise<T> {
    if (!init) {
      init = {} as RequestInit;
    }

    init.headers = new Headers(init.headers);

    try {
      const idToken = await getValidToken();

      if (idToken) {
        init.headers.set("Authorization", `Bearer ${idToken}`);
      }
    } catch (error) {
      console.log({
        msg: "customFetch - no valid token",
        error,
      });
    }

    const response = await fetch((API_URL! + pathname).replace("mekidoc.cl", ""), init);

    if (!response.ok) {
      const error = new FetchError("fetch error", response.status);

      error.data = await parseErrorBody(response);
      throw error;
    }

    // response.json() consumes the body even when it rejects, so the raw text
    // has to be read from a clone or it is gone by the time we need it.
    const unparsedResponse = response.clone();

    try {
      // A 204 has no body: the caller gets null, whatever T it asked for.
      if (response.status === 204) return null as T;
      const json = await response.json();

      return json as T;
    } catch {
      const error = new FetchError("invalid json", response.status);

      error.data = await unparsedResponse.text();
      throw error;
    }
  }

  return <FetchContext.Provider value={customFetch}>{children}</FetchContext.Provider>;
}
